// ── Employee Performance: the widget's own query ─────────────────────
//
// Fetched like salesByStore — a separate request, made only while the widget is on the board — but
// keyed under the page's main dashboard query, so the page's Refresh action
// (invalidateQueries({ queryKey: [prefix] })) refetches the employee figures along with the rest.

import { useQuery, keepPreviousData } from '@tanstack/react-query'
import type { PlaceholderDataFunction, UseQueryResult } from '@tanstack/react-query'
import { useAuthStore } from '../../stores/authStore'
import { buildEmployeePerformanceUrl, employeeWantsManualGrants, fetchEmployeePerformance } from '../../lib/employeePerformance'
import type { EmployeePerformanceData, EmployeePerformanceQueryContext, EmployeePerformanceSettings } from '../../lib/employeePerformance'

/** The page's period, filters and query key — the same context it hands the widget — and the query's own options. */
export interface UseEmployeePerformanceOptions extends EmployeePerformanceQueryContext {
    /** The page's own gate for its side queries AND the widget being on the board. */
    enabled: boolean
    /**
     * The widget's settings. Only read for whether the Manual grants column is showing: those
     * figures take a second query, so they are requested only then (unless filters says otherwise).
     */
    settings?: EmployeePerformanceSettings
    /** The page's auto-refresh interval; Custom range never auto-refreshes. */
    refetchInterval?: number | false
}

/**
 * While a query that wants Manual grants loads, the last figures may stand in only if they carried
 * grants too. Figures fetched without them have no grants to show and lack the employees who only
 * granted bucks, so the widget shows its loading state instead until the grants land.
 */
const keepPreviousIfItHadGrants: PlaceholderDataFunction<EmployeePerformanceData, Error> = (previousData, previousQuery) =>
    previousQuery?.queryKey[3] === true ? previousData : undefined

export function useEmployeePerformance({
    enabled, period, filters, settings, queryKeyPrefix, refetchInterval = false,
}: UseEmployeePerformanceOptions): UseQueryResult<EmployeePerformanceData, Error> {
    const token = useAuthStore(s => s.accessToken)
    const wantGrants = filters.includeManualGrants ?? employeeWantsManualGrants(settings)
    const url = buildEmployeePerformanceUrl(period, { ...filters, includeManualGrants: wantGrants })
    return useQuery<EmployeePerformanceData, Error>({
        // The URL carries the flag already; it sits in the key as well so the placeholder rule can read it.
        queryKey: [queryKeyPrefix, 'employee-performance', url, wantGrants],
        queryFn: () => fetchEmployeePerformance(url, token),
        enabled,
        refetchInterval,
        placeholderData: wantGrants ? keepPreviousIfItHadGrants : keepPreviousData,
        retry: false,
    })
}
